import assert from 'minimalistic-assert';
import proxyFreeze from 'proxy-freeze';
import lodash from 'lodash';
import { isNumberUnit, UnitType } from '@exodus/currency';
import AddressSet from '../address-set/index.js';
import { unitTypeToJSON } from './utils.js';
import { ModelIdSymbol } from '../constants.js';
import { createIsInstance, omitUndefined } from '../utils.js';
const { isEqual, isEmpty, isString, merge, omit, mapValues } = lodash;
const FACTORY_SYMBOL = Symbol('Tx');
function parseCurrency(val, currency) {
    assert(currency instanceof UnitType, 'Currency must be an instance of UnitType');
    if (isNumberUnit(val))
        return val;
    return currency.parse(typeof val === 'string' ? val : val.value + ' ' + val.unit);
}
const coerceToObject = (obj) => (obj instanceof Tx ? obj.toJSON() : obj);
export default class Tx {
    version = 1;
    currencies;
    txId;
    error;
    date;
    confirmations;
    selfSend;
    from;
    tokens;
    data;
    meta;
    addresses;
    to;
    dropped;
    coinName;
    feeCoinName;
    coinAmount;
    feeAmount;
    constructor({ json, initSymbol }) {
        const { txId, error, date, confirmations, dropped, coinAmount, coinName, feeAmount, feeCoinName, selfSend, from, to, meta, data, tokens, token, addresses, currencies, } = json;
        assert(initSymbol === FACTORY_SYMBOL, 'Tx: please use Tx.fromJSON().');
        assert(!isEmpty(currencies), 'Tx: `currencies` is required');
        assert(!from || Array.isArray(from), 'Tx: `from` has to be an array.');
        assert(!to || isString(to), 'Tx: `to` has to be a string.');
        assert(!tokens || Array.isArray(tokens), 'Tx: `tokens` has to be an array.');
        assert(!addresses || Array.isArray(addresses) || addresses instanceof AddressSet, 'Tx: unknown `addresses` type');
        assert(!coinAmount || coinName, 'Tx: `coinName` is required with `coinAmount`');
        assert(!feeAmount || feeCoinName, 'Tx: `feeCoinName` is required with `feeAmount`');
        this.currencies = mapValues(currencies, (currency) => currency instanceof UnitType ? currency : UnitType.create(currency));
        this.txId = txId;
        this.error = error || null;
        this.date = date ? new Date(date) : new Date();
        this.confirmations = typeof confirmations === 'number' ? confirmations : -1;
        if (dropped !== undefined)
            this.dropped = !!dropped && this.confirmations <= 0;
        if (coinAmount) {
            assert(this.currencies[coinName], `Tx: currency for ${coinName} missing`);
            this.coinAmount = parseCurrency(coinAmount, this.currencies[coinName]);
            this.coinName = coinName;
        }
        if (feeAmount) {
            assert(this.currencies[feeCoinName], `Tx: currency for ${feeCoinName} missing`);
            this.feeAmount = parseCurrency(feeAmount, this.currencies[feeCoinName]);
            this.feeCoinName = feeCoinName;
        }
        this.selfSend = selfSend || false;
        this.from = from || [];
        if (to)
            this.to = to;
        this.tokens = tokens || (token ? [token] : []);
        this.data = data || Object.create(null);
        this.meta = meta ? { ...meta } : Object.create(null);
        this.addresses = Array.isArray(addresses)
            ? AddressSet.fromArray(addresses)
            : addresses instanceof AddressSet
                ? addresses
                : AddressSet.EMPTY;
    }
    static get [ModelIdSymbol]() {
        return 'Tx';
    }
    static isInstance = createIsInstance(Tx);
    static [Symbol.hasInstance](instance) {
        return this.isInstance(instance);
    }
    static fromJSON(jsonOrString) {
        const json = typeof jsonOrString === 'string' ? JSON.parse(jsonOrString) : jsonOrString;
        const tx = new Tx({ json, initSymbol: FACTORY_SYMBOL });
        return proxyFreeze(tx);
    }
    clone() {
        return Tx.fromJSON(this);
    }
    equals(otherTx) {
        return this === otherTx || isEqual(this.toJSON(), otherTx.toJSON());
    }
    toJSON() {
        const _obj = {
            __proto__: null,
            ...this,
            date: this.date.toISOString(),
            addresses: [...this.addresses].map((address) => address.toJSON()),
            currencies: mapValues(this.currencies, unitTypeToJSON),
            ...(this.coinAmount && { coinAmount: this.coinAmount.toDefaultString({ unit: true }) }),
            ...(this.feeAmount && { feeAmount: this.feeAmount.toDefaultString({ unit: true }) }),
        };
        const obj = _obj;
        if (obj.addresses.length === 0)
            delete obj.addresses;
        if (!obj.selfSend)
            delete obj.selfSend;
        if (obj.from.length === 0)
            delete obj.from;
        if (Object.keys(obj.data).length === 0)
            delete obj.data;
        if (!obj.error)
            delete obj.error;
        if (Object.keys(obj.meta).length === 0)
            delete obj.meta;
        if (obj.tokens.length === 0)
            delete obj.tokens;
        return omitUndefined(obj);
    }
    toString() {
        return String(this.txId);
    }
    update(fields) {
        const current = this.toJSON();
        const data = coerceToObject(fields);
        const updateable = omit(data, ['version']);
        const isNoop = Object.keys(updateable).every((field) => isEqual(current[field], updateable[field]));
        if (isNoop) {
            return this;
        }
        return Tx.fromJSON(merge(Object.create(null), current, updateable));
    }
    get accepted() {
        return (this.received || (this.sent && this.confirmations >= 0)) && !this.dropped;
    }
    get confirmed() {
        return this.confirmations > 0;
    }
    get pending() {
        return this.confirmations <= 0 && !this.dropped;
    }
    get failed() {
        return this.error || this.dropped;
    }
    get sent() {
        return !!(this.coinAmount?.isNegative || this.to || this.selfSend);
    }
    get received() {
        return !this.sent;
    }
    get token() {
        return this.tokens[0] || null;
    }
}
